import React, { ReactElement, useMemo, memo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { captureException } from '_utils/Sentry';
import useNativeNotificationClickHandling from './hooks/useNativeNotificationClickHandling';
import useNavigateToAllowPush from './hooks/useNavigateToAllowPush';
import RenderConfiguration from './RenderConfiguration';
import { useOwnProfile } from '../_api/useProfile';
import { useRegisterUserContext, useSelectedCoopContext } from '../_utils/Tracking';
import { AppErrorScreen } from '../Components';
import SimpleLoadScreen from '../Screens/SimpleLoadScreen';

const Navigation = (): ReactElement => {
    const { isLoading, status, data: profile, error } = useOwnProfile();

    useNativeNotificationClickHandling();
    useSelectedCoopContext();
    useRegisterUserContext();

    const initialRoute = useMemo(() => {
        if (!profile?.gdpr) {
            return 'Onboarding';
        }
        return 'Main';
    }, [profile]);

    useNavigateToAllowPush(initialRoute === 'Onboarding');

    if (isLoading || status === 'pending') {
        return <SimpleLoadScreen />;
    }

    if (!profile && error) {
        throw error;
    }

    if (!profile) {
        captureException(new Error('Unexpected state: No profile after loading'));
    }

    return (
        <ErrorBoundary FallbackComponent={AppErrorScreen}>
            <RenderConfiguration initialRoute={initialRoute} />
        </ErrorBoundary>
    );
};

export default memo(Navigation);
